/* Shop4Seats — collection page (deferred, only loaded by sections/main-collection).
 * Filters / sort / pages: build the URL, fetch ?section_id=…, swap every [data-region] and push history.
 * Mobile: bottom sheet (filter | sort). Paging / load more / infinite: snippets/pagination + pagination.js.
 * Desktop: Large / Compact density (remembered), price slider, inline "Shop by vehicle" box.
 * Also drives sections/main-search: data-keep lists URL params to carry over (q, type…). */
(() => {
  if (customElements.get('s4s-collection')) return;
  const mq = matchMedia('(max-width: 989px)');

  class Collection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => { if (e.target.matches('[data-price-min], [data-price-max]')) this.syncPrice(e.target); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeSheet(); });
      window.addEventListener('popstate', () => this.load(location.href, false));
      document.addEventListener('s4s:vehicle-change', (e) => { this.toggleVehicleBox(false); this.applyVehicle(e.detail); });
      this.setDensity(this.classList.contains('is-dense') ? 'dense' : 'comfort', false);
      this.initPrice();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.labelVehicleChips());
      else this.labelVehicleChips();
      this.setStickyTop();
      mq.addEventListener('change', () => this.setStickyTop());
    }

    /* ---------- URL building ---------- */
    paramsFrom(form) {
      const p = new URLSearchParams();
      for (const [k, v] of new FormData(form)) if (v !== '') p.append(k, v);
      const cur = new URL(location.href).searchParams;
      ['sort_by'].concat((this.dataset.keep || '').split(',')).forEach((k) => { const v = cur.get(k); if (k && v) p.set(k, v); });
      return p;
    }
    urlFor(params) { return `${location.pathname}?${params.toString()}`; }

    /* ---------- Fetch + swap regions ---------- */
    async load(url, push = true) {
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', this.sectionId);
      const active = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;
      this.classList.add('s4s-loading');
      try {
        const html = await fetch(u).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('s4s-collection');
        if (!fresh) throw new Error('No section');
        this.querySelectorAll('[data-region]').forEach((el) => {
          const n = fresh.querySelector(`[data-region="${el.dataset.region}"]`);
          if (n) el.replaceWith(n);
        });
        if (push) {
          u.searchParams.delete('section_id');
          history.pushState({}, '', u);
        }
        this.initPrice();
        this.labelVehicleChips();
        if (active) document.getElementById(active)?.focus({ preventScroll: true });
        document.dispatchEvent(new CustomEvent('s4s:cart-updated')); // re-run fit lines / wishlist on new cards
        if (window.S4S && S4S.syncWish) S4S.syncWish();
      } catch (err) {
        location.href = url;
      } finally {
        this.classList.remove('s4s-loading');
      }
    }

    /* ---------- Events ---------- */
    onClick(e) {
      const link = e.target.closest('a[data-filter-link]');
      if (link) {
        e.preventDefault();
        this.load(link.href);
        if (link.closest('.pager__pages')) this.querySelector('.collection__layout')?.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      const d = e.target.closest('[data-density]');
      if (d) return this.setDensity(d.dataset.density, true);
      const open = e.target.closest('[data-sheet-open]');
      if (open) return this.openSheet(open.dataset.sheetOpen);
      if (e.target.closest('[data-sheet-close]')) return this.closeSheet();
      const sortRow = e.target.closest('[data-sort-value]');
      if (sortRow) {
        this.closeSheet();
        return this.sortBy(sortRow.dataset.sortValue);
      }
      if (e.target.closest('[data-vehicle-edit]')) return this.toggleVehicleBox(true);
      if (e.target.closest('[data-vehicle-cancel]')) return this.toggleVehicleBox(false);
      if (e.target.closest('[data-vehicle-clear-all]')) return window.S4S && S4S.vehicle && S4S.vehicle.clear();
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-sort]')) return this.sortBy(t.value);
      const form = t.closest('[data-filter-form]');
      if (!form || t.closest('[data-vehicle-form]')) return;
      if (t.matches('[data-price-min], [data-price-max]')) this.commitPrice(t);
      this.load(this.urlFor(this.paramsFrom(form)));
    }

    sortBy(value) {
      const u = new URL(location.href);
      u.searchParams.set('sort_by', value);
      u.searchParams.delete('page');
      this.load(u.toString());
    }

    /* ---------- Density ---------- */
    setDensity(v, save) {
      this.classList.toggle('is-dense', v === 'dense');
      this.querySelectorAll('[data-density]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.density === v ? 'true' : 'false'));
      if (save) { try { localStorage.setItem('s4s_density', v); } catch (err) { /* storage blocked */ } }
    }

    /* ---------- Price slider ---------- */
    initPrice() {
      this.querySelectorAll('[data-price]').forEach((box) => this.syncPrice(box.querySelector('[data-price-min]')));
    }
    syncPrice(input) {
      if (!input) return;
      const box = input.closest('[data-price]');
      const min = box.querySelector('[data-price-min]');
      const max = box.querySelector('[data-price-max]');
      const floor = Number(box.dataset.floor);
      const ceil = Number(box.dataset.ceil);
      let lo = Number(min.value);
      let hi = Number(max.value);
      if (lo > hi - 25) { if (input === min) lo = min.value = hi - 25; else hi = max.value = lo + 25; }
      const span = ceil - floor || 1;
      const track = box.querySelector('.cprice__track');
      track.style.setProperty('--lo', `${((lo - floor) / span) * 100}%`);
      track.style.setProperty('--hi', `${((ceil - hi) / span) * 100}%`);
      box.querySelector('[data-price-label]').textContent = `$${lo.toLocaleString('en-US')} – $${hi.toLocaleString('en-US')}${hi >= ceil ? '+' : ''}`;
    }
    commitPrice(input) {
      const box = input.closest('[data-price]');
      const lo = Number(box.querySelector('[data-price-min]').value);
      const hi = Number(box.querySelector('[data-price-max]').value);
      box.querySelector('[data-price-gte]').value = lo > Number(box.dataset.floor) ? lo : '';
      box.querySelector('[data-price-lte]').value = hi < Number(box.dataset.ceil) ? hi : '';
    }

    /* ---------- Bottom sheet (mobile) ---------- */
    openSheet(kind) {
      const sheet = this.querySelector('[data-sheet]');
      sheet.querySelector('[data-sheet-title]').textContent = kind === 'sort' ? 'Sort by' : 'Filter';
      sheet.querySelectorAll('[data-sheet-pane]').forEach((p) => { p.hidden = p.dataset.sheetPane !== kind; });
      sheet.querySelector('[data-sheet-foot]').hidden = kind === 'sort';
      sheet.hidden = false;
      this.sheetOpener = document.activeElement;
      if (window.S4S && S4S.lock) S4S.lock(true);
      sheet.querySelector('.csheet__close').focus();
    }
    closeSheet() {
      const sheet = this.querySelector('[data-sheet]');
      if (!sheet || sheet.hidden) return;
      sheet.hidden = true;
      if (window.S4S && S4S.lock) S4S.lock(false);
      this.sheetOpener?.focus?.();
    }

    /* ---------- "Shop by vehicle" box (desktop) ---------- */
    toggleVehicleBox(edit) {
      const has = !!(window.S4S && S4S.vehicle && S4S.vehicle.get());
      const row = this.querySelector('[data-vehicle-row]');
      const box = this.querySelector('[data-vehicle-box]');
      if (!row || !box) return;
      row.hidden = !has || edit;
      box.hidden = has && !edit;
      if (edit) box.querySelector('select:not([disabled])')?.focus();
    }

    /* Saving a vehicle filters the grid by its type (storefront filter on product metafield fitment.vehicle_types,
     * value = vehicle_type metaobject GID); clearing it removes that filter. */
    applyVehicle(v) {
      const P = 'filter.p.m.fitment.vehicle_types';
      const u = new URL(location.href);
      const cur = u.searchParams.get(P);
      const want = v && v.ti ? v.ti : null;
      if (cur === want || (!cur && !want)) return;
      u.searchParams.delete(P);
      u.searchParams.delete('page');
      if (want) u.searchParams.set(P, want);
      this.load(u.toString());
    }

    /* Vehicle-type chip reads "2024 Newmar Bay Star" when it is the saved vehicle's type (server text: "Fits <type>") */
    labelVehicleChips() {
      const v = window.S4S && S4S.vehicle && S4S.vehicle.get();
      this.querySelectorAll('[data-chip-vehicle]').forEach((el) => {
        if (v && v.ti === el.dataset.chipVehicle) el.textContent = S4S.vehicle.label(v);
      });
    }

    setStickyTop() {
      const h = document.querySelector('.header__main');
      if (h) this.style.setProperty('--header-sticky-h', `${h.offsetHeight}px`);
    }
  }
  customElements.define('s4s-collection', Collection);
})();
